import { Form } from '@ant-design/compatible';
import { InputNumber } from 'antd';
import { FormikProps } from 'formik';
import React from 'react';

import EnumSelect from '../../../components/EnumSelect';
import {
  AccessPoint,
  AccessPointType_enum,
} from '../../../graphql/hasura/generated';
import { FORM_ITEM_DEFAULT_LAYOUT } from '../../../pages/ModelForm/form-fields';
import { getFieldHelpText, getPropertyFeatureFlags } from '../../../utils';
import { AccessPointRoleManager } from '../../AccessPointRole/components/AccessPointRoleManager';
import { useAccessPointRolesQuery } from '../../AccessPointRole/hooks/useAccessPointRolesQuery';
import { AccessPointRoleModel } from '../../AccessPointRole/model';

import AccessPointTypeDescription from './AccessPointTypeDescription';

interface IAccessPointTypeManagerProps {
  formikProps: FormikProps<AccessPoint>;
  isNew: boolean;
}

const AccessPointTypeManager: React.FC<IAccessPointTypeManagerProps> = (props) => {
  const { formikProps, isNew } = props;
  const accessPoint = formikProps.values;
  const { accessPointId, type, maxOccupancy, archivedAt } = accessPoint;

  const {
    accessPointRoles,
    roles,
    refetch,
  } = useAccessPointRolesQuery({ accessPointId, type, skip: isNew });

  const help = getFieldHelpText(formikProps, 'maxOccupancy');
  const validateStatus = help ? 'error' : undefined;

  const manageRolesEnabled = (
    !isNew &&
    formikProps.initialValues.type === type &&
    (
      type === AccessPointType_enum.COMMON_AREA ||
      type === AccessPointType_enum.RESTRICTED_WHITELIST ||
      type === AccessPointType_enum.RESTRICTED_BLACKLIST ||
      !!accessPointRoles.length
    )
  );

  const stratisProperty = getPropertyFeatureFlags(accessPoint.property).COMMUNITY_CONNECT;

  return (
    <>
      <Form.Item
        {...FORM_ITEM_DEFAULT_LAYOUT}
        label='Type'
        required
      >
        <EnumSelect
          enumTable='AccessPointType'
          value={type}
          disabled={!!archivedAt}
          onChange={nextValue => {
            formikProps.setFieldValue('type', nextValue);

            if (nextValue !== AccessPointType_enum.RESERVATION_ONLY) {
              formikProps.setFieldValue('maxOccupancy', null);
            }
          }}
          formatOptions={options => {
            if (!stratisProperty) {
              return options;
            }

            return options.filter(
              (option) => option.value !== AccessPointType_enum.RESERVATION_ONLY
            );
          }}
        />
        <AccessPointTypeDescription type={type} />
      </Form.Item>

      {manageRolesEnabled && (
        <Form.Item
          {...FORM_ITEM_DEFAULT_LAYOUT}
          label={<div />} // Workaround to align the table within the layout
          colon={false}
        >
          <AccessPointRoleManager
            accessPoint={accessPoint}
            accessPointRoles={accessPointRoles}
            roles={roles}
            refetch={refetch}
            readOnly={(
              !!accessPoint.archivedAt || (
                !AccessPointRoleModel.permissions.canCreate() &&
                !AccessPointRoleModel.permissions.canUpdate() &&
                !AccessPointRoleModel.permissions.canDelete()
              )
            )}
          />
        </Form.Item>
      )}

      {type === AccessPointType_enum.RESERVATION_ONLY && (
        <Form.Item
          {...FORM_ITEM_DEFAULT_LAYOUT}
          label='Max Occupancy'
          validateStatus={validateStatus}
          help={help}
          required
        >
          <InputNumber
            name='maxOccupancy'
            type='number'
            value={maxOccupancy || undefined}
            min={1}
            onChange={nextValue => formikProps.setFieldValue('maxOccupancy', nextValue)}
            style={{ width: '100%' }}
          />
        </Form.Item>
      )}
    </>
  );
};

export default AccessPointTypeManager;
